import { useController, useFormContext } from "react-hook-form";
import { useSelector } from "src/redux/hooks";
import { useCallback, useMemo } from "react";

import { useLogbookRefsContext } from "../Logbook/LogbookRefsContext";
/**
 * Validates the input value based on the required condition.
 *
 * @param v - The value to validate.
 * @param required - A string or boolean indicating if the input is required.
 * @returns A boolean indicating whether the input is valid.
 */
export const requiredInputValidator = (v: any, required?: string | boolean) => {
  return (
    !required ||
    (required &&
      ((typeof v === "string" && v.trim() !== "") ||
        (typeof v === "boolean" && v) ||
        (typeof v === "object" && v)))
  );
};
/**
 * Custom hook to generate input properties for a form field.
 *
 * @param id - The unique identifier for the form field.
 * @param value - The default value of the form field.
 * @param required - A boolean or string indicating if the field is required.
 * @param exclusive - The exclusive group identifier for validation.
 * @returns An object containing field properties, validation state, and styles.
 */
export const useInputProps = (
  id: string,
  value: any,
  required?: boolean | string,
  exclusive?: string | true,
  customValidator?: (v: any) => boolean,
  exclusiveUser?: string,
) => {
  const uppercase = useSelector(state => state.preferencesSlice.uppercase);
  const { control, setValue, getValues, trigger, formState } = useFormContext();

  const fieldName = useMemo(() => {
    if (exclusiveUser && exclusiveUser.trim() !== "") {
      return `${id}-exclusiveUser-${exclusiveUser}`;
    }
    return exclusive && typeof exclusive === "string" && exclusive.trim() !== ""
      ? `${id}-exclusive-${exclusive}`
      : id;
  }, [id, exclusive, exclusiveUser]);

  exclusive =
    exclusive === true
      ? id?.trim()?.split("-exclusive-")[1]?.split(".")[0]
      : exclusive;

  const hasValidValue = useCallback(
    (value: any) => {
      if (!value || value === undefined) return false;
      if (typeof value === "string") return value.trim() !== "";
      if (typeof value === "boolean") return value;
      if (typeof value === "object") {
        if (Array.isArray(value)) return value.length > 0;
        if (value.userid || value.ref) {
          return true;
        }

        if ("nums" in value || "dir" in value) {
          const hasNums = Boolean(
            value.nums &&
            (typeof value.nums === "string" ? value.nums.trim() !== "" : true),
          );
          const hasDir = Boolean(
            value.dir && ["N", "E", "S", "W"].includes(value.dir),
          );
          const numsValid = customValidator
            ? customValidator(value.nums)
            : hasNums;
          return numsValid && hasDir;
        }

        return Object.values(value).some(
          val => val && typeof val === "string" && val.trim() !== "",
        );
      }
      return Boolean(value); // For numbers and other types
    },
    [customValidator],
  );

  const getUserIdentity = useCallback((userValue: any) => {
    if (!userValue) return "";
    if (typeof userValue === "string") return userValue.trim();
    if (typeof userValue === "object") {
      const identity =
        userValue.userid || userValue.manualValue || userValue.ref || "";
      return typeof identity === "string" ? identity.trim() : String(identity);
    }
    return String(userValue).trim();
  }, []);

  const validateField = useCallback(
    (v: any) => {
      if (exclusiveUser && exclusiveUser.trim() !== "") {
        if (!requiredInputValidator(v, required)) return false;

        const currentUserIdentity = getUserIdentity(v);
        if (!currentUserIdentity) return !required;

        const formValues = getValues();
        const exclusiveUserFields = Object.keys(formValues).filter(key =>
          key.includes(`-exclusiveUser-${exclusiveUser}`),
        );
        const matchingFields = exclusiveUserFields.filter(
          key => getUserIdentity(formValues[key]) === currentUserIdentity,
        );

        return matchingFields.length <= 1;
      }

      if (exclusive && exclusive.trim() !== "" && required) {
        const formValues = getValues();
        const exclusiveFields = Object.keys(formValues).filter(key =>
          key.includes(`-exclusive-${exclusive}`),
        );

        const gpsFields = exclusiveFields.filter(
          key =>
            typeof formValues[key] === "object" &&
            "nums" in formValues[key] &&
            "dir" in formValues[key],
        );
        let gpsValid = true;
        let anyFieldHasValue = false;
        exclusiveFields
          .filter(
            key =>
              !(
                typeof formValues[key] === "object" &&
                ("nums" in formValues[key] || "dir" in formValues[key])
              ),
          )
          .forEach((key: string) => {
            const fieldValue = formValues[key];
            const isValid = hasValidValue(fieldValue);
            anyFieldHasValue ||= Boolean(isValid);
          });
        if (!anyFieldHasValue && gpsFields.length > 0) {
          gpsFields.forEach((key: string) => {
            const fieldValue = formValues[key];
            const isValid = hasValidValue(fieldValue);
            gpsValid &&= Boolean(isValid);
          });
          anyFieldHasValue ||= Boolean(gpsValid);
        }
        return anyFieldHasValue;
      }

      const isValid = customValidator
        ? customValidator(v)
        : requiredInputValidator(v, required);

      return isValid;
    },
    [
      required,
      exclusive,
      exclusiveUser,
      getValues,
      hasValidValue,
      getUserIdentity,
      customValidator,
    ],
  );

  const {
    field,
    fieldState: { invalid, isTouched },
  } = useController({
    control,
    name: fieldName,
    defaultValue: value,
    rules: {
      required:
        exclusive && exclusive.trim() !== "" ? false : required === true,
      validate: validateField,
    },
  });

  const hasUserInteracted =
    isTouched ||
    formState.isSubmitted ||
    Object.keys(formState.errors).length > 0;
  const shouldShowError = invalid && hasUserInteracted;

  const refProps = useLogbookRefsContext(fieldName);

  const customOnChange = useCallback(
    (event: any) => {
      field.onChange(event, exclusive, required);
      if (exclusiveUser && exclusiveUser.trim() !== "") {
        setTimeout(() => {
          const formValues = getValues();
          Object.keys(formValues).forEach((key: string) => {
            if (key.includes(`-exclusiveUser-${exclusiveUser}`)) {
              void trigger(key);
            }
          });
        }, 10);
      } else if (exclusive && exclusive.trim() !== "") {
        setTimeout(() => {
          const formValues = getValues();
          Object.keys(formValues).forEach((key: string) => {
            if (key.includes(`-exclusive-${exclusive}`)) {
              void trigger(key);
            }
          });
        }, 10);
      } else if (required) {
        if (shouldShowError) {
          setTimeout(() => {
            void trigger(fieldName);
          }, 10);
        }
      }
    },
    [
      exclusive,
      exclusiveUser,
      required,
      getValues,
      trigger,
      fieldName,
      field,
      shouldShowError,
    ],
  );

  return {
    ...field,
    onChange: customOnChange, // Override the default onChange
    ...refProps,
    touched: isTouched,
    error: shouldShowError,
    invalid,
    setValue,
    styles: {
      input: { textTransform: uppercase ? "uppercase" : "none" },
      option: {
        textTransform: uppercase ? "uppercase" : "none",
        wordBreak: "keep-all",
      },
      pill: { textTransform: uppercase ? "uppercase" : "none" },
    } as const,
  };
};
